import React from 'react';
import PropTypes from 'prop-types';
import Modal from '@nexus/fff-ui-shared/Modal';
import CloseButton from '@nexus/fff-ui-shared/CloseButton';
import Button from '@nexus/fff-ui-shared/Button';

import classNames from 'classnames';
import { get } from 'lodash';

import { PRODUCT_PURCHASE_OPTIONS } from 'utils/subscriptions';

import styles from './SubscriptionModalConfirmation.module.scss';

const MODAL_BODY_MESSAGES = {
  UPDATE_SUBSCRIBE_TO_ONE: {
    title: 'Are you Sure?',
    body: "You're about to replace your subscription for a one-time purchase. (Subscription quantities are limited, you may not have an opportunity to re-subscribe.)",
    hasSingleBtn: false,
  },
  UPDATE_SUBSCRIBE_TO_ONE_OOS: {
    title: 'Oh No!',
    body: 'This product is no longer available as a one-time purchase. If you would like to keep your subscription, you will need to re-subscribe.',
    hasSingleBtn: true,
  },
  UPDATE_ONE_TO_SUBSCRIBE: {
    title: 'Are you Sure?',
    body: "You're about to replace your one-time purchase for a subscription. (Quantities are limited, you may not have an opportunity to add this again.)",
    hasSingleBtn: false,
  },
  UPDATE_ONE_TO_SUBSCRIBE_OOS: {
    title: 'Oh No!',
    body: 'This product is no longer available as a subscription. If you would like to keep your one-time purchase, you will need to add it again.',
    hasSingleBtn: true,
  },
};

const SubscriptionModalConfirmation = ({
  isOpen,
  productPurchaseOption,
  isOneTimePurchaseAvailable,
  isSubscriptionAvailable,
  onClose,
  onConfirm,
}) => {
  const setContentKey = () => {
    if (
      productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.ONE_TIME &&
      isOneTimePurchaseAvailable
    ) {
      return 'UPDATE_SUBSCRIBE_TO_ONE';
    }
    if (
      productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.ONE_TIME &&
      !isOneTimePurchaseAvailable
    ) {
      return 'UPDATE_SUBSCRIBE_TO_ONE_OOS';
    }
    if (
      productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE &&
      isSubscriptionAvailable
    ) {
      return 'UPDATE_ONE_TO_SUBSCRIBE';
    }
    if (
      productPurchaseOption === PRODUCT_PURCHASE_OPTIONS.SAVE_SUBSCRIBE &&
      !isSubscriptionAvailable
    ) {
      return 'UPDATE_ONE_TO_SUBSCRIBE_OOS';
    }

    return null;
  };

  const getKey = setContentKey();
  const content = get(MODAL_BODY_MESSAGES, getKey, {});

  return (
    <Modal isOpen={isOpen} className={styles.modal}>
      <CloseButton onClick={onClose} className={styles.closeButton} />
      <div
        className={styles.container}
        data-testid="subscription-modal-container"
      >
        <i className="far fa-shopping-cart" />
        <h3>{content.title}</h3>
        <p>{content.body}</p>
        <div
          className={classNames(styles.actionsContainer, {
            [styles.actionsContainerSingleBtn]: content.hasSingleBtn,
          })}
        >
          {content.hasSingleBtn ? (
            <Button
              data-testid="ok-subscription-btn"
              onClick={onClose}
              width="50%"
            >
              OK, GOT IT
            </Button>
          ) : (
            <>
              <Button
                data-testid="no-subscription-btn"
                onClick={onClose}
                width="48%"
                variation="black-outline"
              >
                NO
              </Button>
              <Button
                data-testid="yes-subscription-btn"
                onClick={onConfirm}
                width="48%"
              >
                YES
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

SubscriptionModalConfirmation.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  productPurchaseOption: PropTypes.string.isRequired,
  isOneTimePurchaseAvailable: PropTypes.bool.isRequired,
  isSubscriptionAvailable: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default SubscriptionModalConfirmation;
